
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { CountyHeader } from "@/components/nursing-homes/county/CountyHeader";
import { SearchSection } from "@/components/nursing-homes/county/SearchSection";
import { NursingHomeResults } from "@/components/nursing-homes/county/NursingHomeResults";
import { CountyCTASection } from "@/components/nursing-homes/county/CountyCTASection";
import { useNursingHomes } from "@/hooks/use-nursing-homes";
import { useCounties } from "@/hooks/use-counties";
import { SEOHead } from "@/components/seo/SEOHead";
import { generateBreadcrumbSchema } from "@/lib/schema";
import { BreadcrumbNavigation } from "@/components/ui/breadcrumb-list";

export default function CountyPage() {
  const navigate = useNavigate();
  const { countySlug } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("name_asc");
  const [selectedCounty, setSelectedCounty] = useState<string | null>(countySlug || null);
  
  // Fetch counties for the dropdown
  const { data: counties, isLoading: countiesLoading } = useCounties();
  
  // Format county name from slug
  const formatCountyName = (slug?: string) => {
    if (!slug) return '';
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const countyName = formatCountyName(countySlug);
  
  // Fetch nursing homes based on current filters
  const { data: nursingHomes, isLoading, error } = useNursingHomes(selectedCounty || countySlug, searchQuery, sortBy);

  // Update the URL if the selected county changes
  useEffect(() => {
    if (selectedCounty && selectedCounty !== countySlug) {
      navigate(`/nursing-homes/${selectedCounty}`);
    }
  }, [selectedCounty, navigate, countySlug]);

  // Create breadcrumbs for this page
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Nursing Homes", url: "/nursing-homes" },
    { name: countyName, url: `/nursing-homes/${countySlug}` }
  ];

  console.log("CountyPage rendering with:", { countySlug, nursingHomes: nursingHomes?.length });

  // Generate breadcrumb schema
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);

  const metaDescription = `Find and compare nursing homes in ${countyName}, Ireland. Detailed information on facilities, services, and Fair Deal scheme support for elderly care in ${countyName}.`;

  // Format counties for dropdown
  const countyOptions = counties?.map(county => ({
    county: county.name,
    county_slug: county.slug
  })) || [];

  return (
    <Layout>
      <SEOHead 
        title={`Nursing Homes in ${countyName}, Ireland | Grandpal`}
        description={metaDescription}
        canonicalUrl={`/nursing-homes/${countySlug}`}
        schema={breadcrumbSchema}
      />
      
      <CountyHeader countyName={countyName} />
      
      <div className="container py-12">
        <div className="mb-6">
          <BreadcrumbNavigation items={breadcrumbs.map(b => ({ label: b.name, href: b.url }))} />
        </div>
        
        <SearchSection 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          selectedCounty={selectedCounty}
          setSelectedCounty={setSelectedCounty}
          counties={countyOptions}
          countiesLoading={countiesLoading}
        />
        
        <NursingHomeResults 
          isLoading={isLoading}
          error={error as Error}
          nursingHomes={nursingHomes}
          countyName={countyName}
          countySlug={selectedCounty || countySlug || ''}
          searchQuery={searchQuery}
        />
      </div>
      
      <CountyCTASection countyName={countyName} />
    </Layout>
  );
}
